import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, functions, db } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { 
  doc, setDoc, serverTimestamp, onSnapshot, collection, getDoc 
} from 'firebase/firestore';

const getFilteredSchema = async (email) => {
  try {
    const getSchema = httpsCallable(functions, 'getFilteredSchema');
    const result = await getSchema({ email });
    if (!result.data) {
      throw new Error('No schema data received');
    }
    return result.data;
  } catch (error) {
    throw error;
  }
};

const AuthContext = createContext();
const REGISTRATION_CACHE_KEY = 'registration_status';
const ALLOWED_TABLES_CACHE_KEY = 'allowed_tables';

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [registrationChecked, setRegistrationChecked] = useState(false);
  const [allowedTables, setAllowedTables] = useState(null);
  const [unsubscribers, setUnsubscribers] = useState([]);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const [twoFactorChecked, setTwoFactorChecked] = useState(false);


  // Cache management functions
  const getCachedRegistration = (email) => {
    try {
      const cached = localStorage.getItem(REGISTRATION_CACHE_KEY + '_' + email);
      if (cached) {
        const { status, userData } = JSON.parse(cached);
        return { status, userData };
      }
    } catch (error) {
    }
    return null;
  };

  const setCachedRegistration = (email, status, userData = null) => {
    try {
      localStorage.setItem(
        REGISTRATION_CACHE_KEY + '_' + email,
        JSON.stringify({ status, userData })
      );
    } catch (error) {
    }
  };

  const clearRegistrationCache = (email) => {
    try {
      localStorage.removeItem(REGISTRATION_CACHE_KEY + '_' + email);
    } catch (error) {
    }
  };

  async function checkUserRegistration(email) {
    if (!email) {
      setIsRegistrationComplete(false);
      setRegistrationChecked(true);
      return { isRegistrationComplete: false };
    }
    
    // Check cache first
    const cachedData = getCachedRegistration(email);
    if (cachedData !== null) {
      // Add validation of required fields
      const isComplete = cachedData.status && 
        cachedData.userData && 
        cachedData.userData.englishName && 
        cachedData.userData.role;
      setIsRegistrationComplete(isComplete);
      setRegistrationChecked(true);
      return { 
        data: { 
          isRegistrationComplete: isComplete,
          userData: cachedData.userData
        }
      };
    }
  
    try {
      const checkRegistration = httpsCallable(functions, 'checkUserRegistration');
      const result = await checkRegistration({ email });
      
      // Add validation of required fields
      const isComplete = result.data && 
        result.data.isRegistrationComplete && 
        result.data.userData && 
        result.data.userData.englishName && 
        result.data.userData.role;
      
      setIsRegistrationComplete(isComplete);
      setRegistrationChecked(true);
      
      // Cache the result with user data
      setCachedRegistration(email, isComplete, result.data.userData);
      
      return { 
        data: { 
          isRegistrationComplete: isComplete,
          userData: result.data.userData
        }
      };
    } catch (error) {
      setIsRegistrationComplete(false);
      setRegistrationChecked(true);
      return { data: { isRegistrationComplete: false } };
    }
  }

  const setupRealtimeListeners = useCallback(async (userEmail) => {
    
    // Clear existing listeners
    unsubscribers.forEach(unsub => unsub());
    setUnsubscribers([]);
    
    if (!userEmail) {
        return;
    }
  
    const newUnsubscribers = [];
  
    // Listen for table configuration changes
    const tablesUnsubscribe = onSnapshot(
        doc(db, 'appConfiguration', 'tables'),
        async (snapshot) => {
            try {
                // Use the passed userEmail instead of relying on currentUser
                const newSchema = await getFilteredSchema(userEmail);
                if (newSchema) {
                    setAllowedTables(newSchema);
                }
            } catch (error) {
            }
        },
        (error) => {
        }
    );
    newUnsubscribers.push(tablesUnsubscribe);

    // Enhanced listener for user preferences
    const userPrefsUnsubscribe = onSnapshot(
        doc(db, 'registeredUsersPreferences', userEmail),
        async (snapshot) => {
            
            try {
                // Clear the cache immediately
                clearAllowedTablesCache(userEmail);
                
                // Use the passed userEmail instead of relying on currentUser
                const newSchema = await getFilteredSchema(userEmail);
                if (newSchema) {
                    setAllowedTables(newSchema);
                    // Update the cache with new data
                    setCachedAllowedTables(userEmail, newSchema);
                }
            } catch (error) {
            }
        },
        (error) => {
        }
    );
    newUnsubscribers.push(userPrefsUnsubscribe);

    // Update the roles collection listener
    const rolesUnsubscribe = onSnapshot(
        collection(db, 'roles'),
        async (snapshot) => {
            try {
                // Clear the cache
                clearAllowedTablesCache(userEmail);

                // Add these lines:
                if (currentUser) {
                    await currentUser.getIdToken(true);
                    await checkAdminStatus(currentUser);
                }
                
                // Use the passed userEmail instead of relying on currentUser
                const newSchema = await getFilteredSchema(userEmail);
                if (newSchema) {
                    setAllowedTables(newSchema);
                }
            } catch (error) {
            }
        },
        (error) => {
        }
    );
    newUnsubscribers.push(rolesUnsubscribe);

    setUnsubscribers(newUnsubscribers);
}, []);

  
const fetchAllowedTables = useCallback(async (email = null) => {
  const userEmail = email || currentUser?.email;
  
  if (!userEmail) {
      return null;
  }
  
  try {
      const schema = await getFilteredSchema(userEmail);
      
      if (!schema || Object.keys(schema).length === 0) {
          throw new Error('No schema data received from backend');
      }
  
      
      const tables = Object.keys(schema).reduce((acc, tableId) => {
          acc[tableId] = true;
          return acc;
      }, {});
      
      if (Object.keys(tables).length === 0) {
          throw new Error('No tables found in schema');
      }
      
      // Cache the result
      setCachedAllowedTables(userEmail, tables);
      return tables;
  } catch (err) {
      throw err;
  }
}, [currentUser?.email]);

  // Cleanup listeners on unmount or user change
  useEffect(() => {
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [unsubscribers]);

  
  async function isEmailVerificationRequired() {
    try {
      const configDoc = await getDoc(doc(db, 'appConfiguration', 'core'));
      const verifyEmail = configDoc.exists() ? (configDoc.data().verifyEmail !== false) : true;
      return verifyEmail;
    } catch (error) {
      // Default to true if there's an error
      return true;
    }
  }

  async function is2FARequired(user = null) {
    try {
      const configDoc = await getDoc(doc(db, 'appConfiguration', 'core'));
      const require2FA = configDoc.exists() ? (configDoc.data().require2FA === true) : false;
      
      if (!require2FA) return false;
      
      // Use passed user or fall back to currentUser
      const targetUser = user || currentUser;
      
      // Check if user recently verified email (skip 2FA if so)
      if (targetUser?.email) {
        const userDoc = await getDoc(doc(db, 'registeredUsersPreferences', targetUser.email));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const emailVerifiedAt = userData.emailVerifiedAt;
          
          if (emailVerifiedAt) {
            const verificationTime = emailVerifiedAt.toDate();
            const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000); // 10 minutes ago
            
            // If email was verified within last 10 minutes, skip 2FA
            if (verificationTime > tenMinutesAgo) {
              console.log('✅ Skipping 2FA - email recently verified');
              return false;
            } else {
              // Clean up old timestamp (older than 10 minutes)
              console.log('🧹 Cleaning up old verification timestamp');
              await setDoc(doc(db, 'registeredUsersPreferences', targetUser.email), {
                emailVerifiedAt: null
              }, { merge: true });
            }
          }
        }
      }
      
      return require2FA;
    } catch (error) {
      console.error('Failed to check 2FA requirement:', error);
      return false;
    }
  }
  
  async function check2FAStatus(user) {
    if (!user) {
      setIs2FAVerified(false);
      setTwoFactorChecked(true);
      return { is2FAVerified: false };
    }
  
    try {
      // Check if 2FA is required globally
      const require2FA = await is2FARequired();
      
      if (!require2FA) {
        // If 2FA is not required, consider it verified
        setIs2FAVerified(true);
        setTwoFactorChecked(true);
        return { is2FAVerified: true };
      }
  
      // Check user's last 2FA verification
      const userDoc = await getDoc(doc(db, 'registeredUsersPreferences', user.email));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const last2FAVerification = userData.last2FAVerification;
        
        if (last2FAVerification) {
          // Check if the verification is still valid (30 days by default)
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const verificationDate = last2FAVerification.toDate();
          const isStillValid = verificationDate > thirtyDaysAgo;
          
          setIs2FAVerified(isStillValid);
          setTwoFactorChecked(true);
          return { is2FAVerified: isStillValid };
        }
      }
      
      // No valid 2FA verification found
      setIs2FAVerified(false);
      setTwoFactorChecked(true);
      return { is2FAVerified: false };
      
    } catch (error) {
      console.error('Failed to check 2FA status:', error);
      setIs2FAVerified(false);
      setTwoFactorChecked(true);
      return { is2FAVerified: false };
    }
  }
  
  async function verify2FACode(code) {
    try {
      const verifyTwoFactorCode = httpsCallable(functions, 'verifyTwoFactorCode');
      const result = await verifyTwoFactorCode({ code });
      
      if (result.data.success) {
        setIs2FAVerified(true);
        return { success: true };
      } else {
        return { 
          success: false, 
          error: result.data.error || 'verification_failed' 
        };
      }
    } catch (error) {
      console.error('2FA verification failed:', error);
      return { 
        success: false, 
        error: error.code === 'functions/unauthenticated' ? 'unauthenticated' : 'verification_failed' 
      };
    }
  }
  
  async function send2FACode() {
    try {
      const sendTwoFactorCode = httpsCallable(functions, 'sendTwoFactorCode');
      await sendTwoFactorCode();
      return { success: true };
    } catch (error) {
      console.error('Failed to send 2FA code:', error);
      throw error;
    }
  }
  
  async function sendVerificationEmail() {
    if (currentUser && !currentUser.emailVerified) {
      try {
        
        // Check if we're within rate limits
        const lastSentKey = `verification_email_last_sent_${currentUser.uid}`;
        const lastSent = localStorage.getItem(lastSentKey);
        
        // Rate limit: Only allow sending once every 60 seconds (Firebase's limit is stricter,
        // but this prevents most unnecessary API calls)
        if (lastSent && (Date.now() - parseInt(lastSent)) < 60000) {
          const secondsRemaining = Math.ceil((60000 - (Date.now() - parseInt(lastSent))) / 1000);
          throw new Error(`Please wait ${secondsRemaining} seconds before requesting another email.`);
        }
        
        await sendEmailVerification(currentUser);
        
        // Store the timestamp of successful send
        localStorage.setItem(lastSentKey, Date.now().toString());
        
        return true;
      } catch (error) {
        
        // Handle Firebase specific errors with user-friendly messages
        if (error.code === 'auth/too-many-requests') {
          throw new Error('Too many attempts. Please wait a few minutes before trying again.');
        } else if (error.code === 'auth/internal-error') {
          throw new Error('Server error. Please try again later.');
        } else if (error.message && error.message.includes('Please wait')) {
          // This is our custom rate limit error, so pass it through
          throw error;
        } else {
          throw new Error('Failed to send verification email. Please try again later.');
        }
      }
    } else {
      throw new Error('No user to verify or email already verified');
    }
  }
  
  const getCachedAllowedTables = (email) => {
    try {
      const cached = localStorage.getItem(ALLOWED_TABLES_CACHE_KEY + '_' + email);
      if (cached) {
        const { tables, timestamp } = JSON.parse(cached);
        // Cache expires after 1 hour
        if (Date.now() - timestamp < 3600000) {
          return tables;
        }
      }
    } catch (error) {
    }
    return null;
  };

  const setCachedAllowedTables = (email, tables) => {
    try {
      localStorage.setItem(
        ALLOWED_TABLES_CACHE_KEY + '_' + email,
        JSON.stringify({
          tables,
          timestamp: Date.now()
        })
      );
    } catch (error) {
    }
  };

  const clearAllowedTablesCache = (email) => {
    try {
      localStorage.removeItem(ALLOWED_TABLES_CACHE_KEY + '_' + email);
    } catch (error) {
    }
  };
  
  async function saveUserPreferences(email, preferences) {
    try {
      const savePrefs = httpsCallable(functions, 'saveUserPreferences');
      const result = await savePrefs({
        email,
        englishName: preferences.englishName,
        role: preferences.role,
        preferences: preferences.preferences || '',  // Add preferences
        enablePersonalization: preferences.enablePersonalization  // Add enablePersonalization
      });
  
      // Update registration status and cache
      setIsRegistrationComplete(true);
      setCachedRegistration(email, true, {
        ...preferences,
        userId: email
      });
  
      // Force a token refresh after successful save
      if (currentUser) {
        await currentUser.getIdToken(true);
        await checkAdminStatus(currentUser);
      }
  
      return result.data;
    } catch (error) {
      throw new Error('Failed to save user preferences: ' + error.message);
    }
  }

  async function signup(email, password) {
    try {
      const checkAllowedEmail = httpsCallable(functions, 'checkAllowedEmail');
      const result = await checkAllowedEmail({ email });
  
      if (result.data.allowed) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const setUserRole = httpsCallable(functions, 'addUserRole');
        await setUserRole({ email });
        
        // Check if email verification is required
        const configDoc = await getDoc(doc(db, 'appConfiguration', 'core'));
        const verifyEmail = configDoc.exists() ? (configDoc.data().verifyEmail !== false) : true;
  
        if (verifyEmail) {
          // Send verification email if verification is required
          await sendEmailVerification(userCredential.user);
        }
  
        // Implement a more robust retry mechanism
        const maxRetries = 5;
        const delay = (retryCount) => new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
  
        for (let retries = 0; retries < maxRetries; retries++) {
          try {
            await delay(retries);
            await userCredential.user.getIdToken(true);
            await checkAdminStatus(userCredential.user);
            return userCredential;
          } catch (error) {
            if (retries === maxRetries - 1) throw new Error('Failed to complete signup. Please try again.');
          }
        }
      } else {
        throw new Error('Email not allowed to register');
      }
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email verification is required
      const verificationRequired = await isEmailVerificationRequired();
      
      // Add verification status to the user credential for the signin component to use
      userCredential.verificationRequired = verificationRequired;
      
      return userCredential;
    } catch (error) {
      throw new Error('Failed to log in. Please try again.');
    }
  }

  async function logout() {
    try {
      // Clear listeners first
      unsubscribers.forEach(unsub => unsub());
      setUnsubscribers([]);
      
      if (currentUser?.email) {
        clearRegistrationCache(currentUser.email);
        clearAllowedTablesCache(currentUser.email);
        
        // 🔥 ADD THIS LINE - Clear 2FA status on logout
        await setDoc(doc(db, 'registeredUsersPreferences', currentUser.email), {
          last2FAVerification: null
        }, { merge: true });
      }
      
      await signOut(auth);
      setIsAdmin(false);
      setCurrentUser(null);
      setIsRegistrationComplete(false);
      setRegistrationChecked(false);
      setAllowedTables(null);
      setIs2FAVerified(false);
      setTwoFactorChecked(false);
    } catch (error) {
      throw new Error('Failed to log out. Please try again.');
    }
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error('Failed to reset password. Please try again.');
    }
  }

  async function checkAdminStatus(user) {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const newIsAdmin = !!idTokenResult.claims.admin;
        setIsAdmin(newIsAdmin);
        return newIsAdmin;
      } catch (error) {
        setIsAdmin(false);
        return false;
      }
    } else {
      setIsAdmin(false);
      return false;
    }
  }

  async function forceTokenRefresh() {
    if (currentUser) {
      try {
        await currentUser.getIdToken(true);
        await checkAdminStatus(currentUser);
      } catch (error) {
      }
    }
  }

  async function setAdminRole(email) {
    try {
      const setUserRole = httpsCallable(functions, 'addUserRole');
      await setUserRole({ email });
      await forceTokenRefresh();
    } catch (error) {
      throw new Error('Failed to set admin role. Please try again.');
    }
  }

  
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      setCurrentUser(user);
      
      // Force token refresh first
      await user.getIdToken(true);
      
      // REMOVED: Email verification tracking (we'll do this elsewhere)
      
      // Check verification requirement
      const verificationRequired = await isEmailVerificationRequired();
      
      // Immediately check admin status
      const adminStatus = await checkAdminStatus(user);
      setIsAdmin(adminStatus);

      // Check 2FA status
      await check2FAStatus(user);
      
      // Then handle registration and tables
      const cachedData = getCachedRegistration(user.email);
      if (cachedData !== null) {
        setIsRegistrationComplete(cachedData.status);
        setRegistrationChecked(true);
        
        try {
          const tables = await fetchAllowedTables();
          setAllowedTables(tables);
          await setupRealtimeListeners(user.email);
        } catch (error) {
          setAllowedTables({});
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const [regStatus, tables] = await Promise.all([
            checkUserRegistration(user.email),
            fetchAllowedTables()
          ]);
          
          setIsRegistrationComplete(regStatus.data.isRegistrationComplete);
          setRegistrationChecked(true);
          setAllowedTables(tables);
          await setupRealtimeListeners(user.email);
        } catch (error) {
          setIsRegistrationComplete(false);
          setRegistrationChecked(true);
          setAllowedTables(null);
        } finally {
          setLoading(false);
        }
      }
    } else {
      setCurrentUser(null);
      setIsAdmin(false);
      setIsRegistrationComplete(false);
      setRegistrationChecked(true);
      setAllowedTables(null);
      setIs2FAVerified(false);
      setTwoFactorChecked(true);
      setLoading(false);
      
      // Clear listeners when user logs out
      unsubscribers.forEach(unsub => unsub());
      setUnsubscribers([]);
    }
  });

  return () => {
    unsubscribe();
  };
}, [setupRealtimeListeners, fetchAllowedTables]);


async function markEmailJustVerified(userEmail) {
  try {
    await setDoc(doc(db, 'registeredUsersPreferences', userEmail), {
      emailVerifiedAt: serverTimestamp()
    }, { merge: true });
    console.log('Marked email as just verified for', userEmail);
  } catch (error) {
    console.error('Failed to mark email verification:', error);
  }
}

const value = {
  currentUser,
  isAdmin,
  isRegistrationComplete,
  registrationChecked,
  allowedTables,
  is2FAVerified,
  twoFactorChecked,
  signup,
  login,
  logout,
  resetPassword,
  checkAdminStatus,
  forceTokenRefresh,
  setAdminRole,
  saveUserPreferences,
  checkUserRegistration,
  sendVerificationEmail,
  isEmailVerificationRequired,
  is2FARequired,
  check2FAStatus,
  verify2FACode,
  send2FACode,
  markEmailJustVerified
};

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}